import { SectionInterface } from "../../../store/types/store";

const ITEM_MOUNT_OFFSET_M = 0.05;

export interface RackHeightState {
    minItemY: number | null;
    minHeightMm: number | null;
    isTooShort: boolean;
}

export interface RackHeightValidationResult {
    left: RackHeightState;
    right: RackHeightState;
}

interface RackHeightValidationParams {
    sections: SectionInterface[];
    currentSectionId: string;
    roomHeight: number;
    offsetCeiling: number;
    leftHeight: number;
    rightHeight: number;
}

const getSectionMinItemY = (section?: SectionInterface): number | null => {
    if (!section || section.isIndent || !section.items?.length) {
        return null;
    }

    const positions = section.items
        .filter((item) => !item.isSpirit && Array.isArray(item.position) && Number.isFinite(item.position[1]))
        .map((item) => Math.round((item.position[1] - ITEM_MOUNT_OFFSET_M) * 1000) / 1000);

    return positions.length ? Math.min(...positions) : null;
};

const getLowestItemY = (...values: Array<number | null>): number | null => {
    const finiteValues = values.filter((value): value is number => value !== null && Number.isFinite(value));
    return finiteValues.length ? Math.min(...finiteValues) : null;
};

const buildRackState = (
    rackHeight: number,
    minItemY: number | null,
    roomHeight: number,
    offsetCeiling: number
): RackHeightState => {
    if (minItemY === null) {
        return {
            minItemY: null,
            minHeightMm: null,
            isTooShort: false,
        };
    }

    const rackBottomY = (roomHeight - offsetCeiling - rackHeight) / 1000;

    const minHeightMm = Math.max(0, Math.trunc(roomHeight - offsetCeiling - minItemY * 1000));

    return {
        minItemY,
        minHeightMm,
        isTooShort: rackBottomY > minItemY,
    };
};

export const getRackHeightValidation = ({
    sections,
    currentSectionId,
    roomHeight,
    offsetCeiling,
    leftHeight,
    rightHeight,
}: RackHeightValidationParams): RackHeightValidationResult => {
    const currentSectionIndex = sections.findIndex((section) => section.id === currentSectionId);

    if (currentSectionIndex === -1) {
        return {
            left: buildRackState(leftHeight, null, roomHeight, offsetCeiling),
            right: buildRackState(rightHeight, null, roomHeight, offsetCeiling),
        };
    }

    const currentSection = sections[currentSectionIndex];
    const prevSection = sections[currentSectionIndex - 1];
    const nextSection = sections[currentSectionIndex + 1];

    const currentMinItemY = getSectionMinItemY(currentSection);

    const prevMinItemY = prevSection && !prevSection.isIndent ? getSectionMinItemY(prevSection) : null;
    const nextMinItemY = nextSection && !nextSection.isIndent ? getSectionMinItemY(nextSection) : null;

    const leftMinItemY = getLowestItemY(currentMinItemY, prevMinItemY);
    const rightMinItemY = getLowestItemY(currentMinItemY, nextMinItemY);

    return {
        left: buildRackState(leftHeight, leftMinItemY, roomHeight, offsetCeiling),
        right: buildRackState(rightHeight, rightMinItemY, roomHeight, offsetCeiling),
    };
};
